import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { HttpClient, HttpResponse } from '@angular/common/http';
import { Catalog, CatalogDetail, MappedValue, MappedValueBase } from '@entity/catalog';
import { EpicExportConfig } from '@entity/epic-export-config';

@Injectable({
  providedIn: 'root',
})
export class CatalogService {
  private httpClient = inject(HttpClient);

  private readonly baseUrl = `api/1.0/mandanten`;

  getCatalogs(mandantId: number): Observable<Catalog[]> {
    return this.httpClient.get<Catalog[]>(`${this.baseUrl}/${mandantId}/catalogs`);
  }

  getCatalogDetails(mandantId: number, catalogId: number): Observable<CatalogDetail[]> {
    return this.httpClient.get<CatalogDetail[]>(`${this.baseUrl}/${mandantId}/catalogs/${catalogId}`);
  }

  updateCatalogDetails(mandantId: number, catalogId: number, value: CatalogDetail[]): Observable<CatalogDetail[]> {
    return this.httpClient.put<CatalogDetail[]>(`${this.baseUrl}/${mandantId}/catalogs/${catalogId}`, value);
  }

  getVariableMappings(mandantId: number, catalogId: number, detailId = ''): Observable<MappedValue[]> {
    return this.httpClient.get<MappedValue[]>(
      `${this.baseUrl}/${mandantId}/catalogs/${catalogId}/variables/${detailId}`
    );
  }

  updateVariableMappings(
    mandantId: number,
    catalogId: number,
    detailId: number,
    mappedValues: MappedValue[]
  ): Observable<MappedValue[]> {
    return this.httpClient.put<MappedValue[]>(
      `${this.baseUrl}/${mandantId}/catalogs/${catalogId}/variables/${detailId}`,
      mappedValues
    );
  }

  getMeasureMappings(mandantId: number, catalogId: number, detailId = ''): Observable<MappedValue[]> {
    return this.httpClient.get<MappedValue[]>(
      `${this.baseUrl}/${mandantId}/catalogs/${catalogId}/measures/${detailId}`
    );
  }

  updateMeasureMappings(
    mandantId: number,
    catalogId: number,
    detailId: number,
    mappedValues: MappedValue[]
  ): Observable<MappedValue[]> {
    return this.httpClient.put<MappedValue[]>(
      `${this.baseUrl}/${mandantId}/catalogs/${catalogId}/measures/${detailId}`,
      mappedValues
    );
  }

  resetCatalogMapping(mandantId: number, catalogId: number): Observable<unknown> {
    return this.httpClient.post(`${this.baseUrl}/${mandantId}/catalogs/${catalogId}/reset-mapping`, {});
  }

  setCatalogDetailActive(
    mandantId: number,
    catalogId: number,
    detailId: string,
    active: boolean
  ): Observable<CatalogDetail[]> {
    return this.httpClient.put<CatalogDetail[]>(
      `${this.baseUrl}/${mandantId}/catalogs/${catalogId}/details/${detailId}/active`,
      { active }
    );
  }

  getCatalogDetailMappings(mandantId: number, catalogId: number, detailId: string): Observable<MappedValueBase[]> {
    return this.httpClient.get<MappedValueBase[]>(
      `${this.baseUrl}/${mandantId}/catalogs/${catalogId}/details/${detailId}/mappings`
    );
  }

  exportMapping(mandantId: number, catalogId: number): Observable<HttpResponse<Blob>> {
    return this.httpClient.get(`${this.baseUrl}/${mandantId}/catalogs/${catalogId}/mapping-xslx-export`, {
      responseType: 'blob',
      observe: 'response',
    });
  }

  exportEpicMappingCatalog(
    mandantId: number,
    catalogId: number,
    config: EpicExportConfig
  ): Observable<HttpResponse<Blob>> {
    return this.httpClient.post(`${this.baseUrl}/${mandantId}/catalogs/${catalogId}/export-epic-mapping`, config, {
      responseType: 'blob',
      observe: 'response',
    });
  }
}
